import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface Fix {
  id: string
  ruleId: string
  status: string
  mode: string
  rationale: string | null
  confidence: number | null
  output: unknown
  artifact: { reportSnippet?: string; reportLocation?: string; pullRequestUrl?: string } | null
}

export function FixDetailPage() {
  const t = useT()
  const { fixId = '' } = useParams()
  const fix = useQuery({ queryKey: ['fix', fixId], queryFn: () => apiClient.get<Fix>(`/fixes/${fixId}`) })

  if (fix.isLoading) return <p>{t('common.loading')}</p>
  if (fix.isError || !fix.data) return <p>{t('common.error')}</p>

  const { artifact } = fix.data
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-1">{t('fix.detail.title')}</h1>
      <p className="text-sm opacity-70 mb-4">
        {fix.data.ruleId} · {fix.data.mode} · {fix.data.status}
        {fix.data.confidence !== null && <> · confidence {(fix.data.confidence * 100).toFixed(0)}%</>}
      </p>

      {artifact?.reportLocation && (
        <section className="mb-6">
          <h2 className="text-sm font-medium opacity-80 mb-1">{t('fix.detail.location')}</h2>
          <p className="text-sm">{artifact.reportLocation}</p>
        </section>
      )}

      {artifact?.reportSnippet && (
        <section className="mb-6">
          <h2 className="text-sm font-medium opacity-80 mb-1">{t('fix.detail.snippet')}</h2>
          <pre className="text-xs p-3 rounded overflow-auto" style={{ background: 'var(--color-border)' }}>
            {artifact.reportSnippet}
          </pre>
        </section>
      )}

      {artifact?.pullRequestUrl && (
        <p>
          <a href={artifact.pullRequestUrl} target="_blank" rel="noreferrer" className="underline">
            Open pull request →
          </a>
        </p>
      )}

      {fix.data.rationale && (
        <section className="mt-6">
          <h2 className="text-sm font-medium opacity-80 mb-1">{t('fix.detail.rationale')}</h2>
          <p className="text-sm opacity-80">{fix.data.rationale}</p>
        </section>
      )}
    </div>
  )
}
